import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { verticalLifecycleApi, type VerticalSummary } from '@/api/verticalLifecycle';

function verticalLabel(vertical: VerticalSummary): string {
  return vertical.display_name || vertical.vertical_id;
}

function searchText(vertical: VerticalSummary): string {
  return [
    vertical.vertical_id,
    vertical.display_name,
    vertical.launch_mode,
    vertical.customer_home_url,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function VerticalAdminIndexPage() {
  const navigate = useNavigate();
  const [verticals, setVerticals] = useState<VerticalSummary[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    verticalLifecycleApi
      .listAdminVerticals()
      .then((response) => {
        if (cancelled) return;
        const next = response.verticals ?? [];
        if (next.length === 1) {
          navigate(`/admin/verticals/${encodeURIComponent(next[0].vertical_id)}`, { replace: true });
          return;
        }
        setVerticals(next);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load verticals.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const filteredVerticals = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return verticals;
    return verticals.filter((vertical) => searchText(vertical).includes(needle));
  }, [query, verticals]);

  const openVertical = (verticalId: string) => {
    navigate(`/admin/verticals/${encodeURIComponent(verticalId)}`);
  };

  return (
    <div className="h-full min-h-[calc(100vh-56px)] bg-[#0a0a0a] text-gray-200 font-mono">
      <div className="mx-auto max-w-5xl px-6 py-6">
        <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Vertical Admin</div>
            <h1 className="mt-1 text-2xl text-gray-100">Managed verticals</h1>
          </div>
          <label className="w-full text-xs text-gray-500 md:w-auto">
            Search
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="mt-1 block w-full min-w-0 rounded border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-200 outline-none focus:border-[#39FF14]/60 md:w-80 md:min-w-[280px]"
              placeholder="Name, id, launch mode"
            />
          </label>
        </div>

        {loading ? (
          <div className="rounded border border-gray-800 bg-black/20 p-4 text-sm text-gray-500">
            Loading verticals...
          </div>
        ) : error ? (
          <div role="alert" className="rounded border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">
            {error}
          </div>
        ) : verticals.length === 0 ? (
          <div className="rounded border border-gray-800 bg-black/20 p-4 text-sm text-gray-500">
            No manageable verticals are available for this account.
          </div>
        ) : (
          <div className="overflow-hidden rounded border border-gray-800 bg-black/20">
            <div className="hidden grid-cols-[minmax(0,1fr)_120px_minmax(0,1fr)_72px] gap-3 border-b border-gray-800 bg-gray-950/70 px-4 py-2 text-[10px] uppercase tracking-wider text-gray-600 md:grid">
              <div>Vertical</div>
              <div>Mode</div>
              <div>Customer Home</div>
              <div />
            </div>
            <div className="max-h-[62vh] overflow-auto">
              {filteredVerticals.length === 0 ? (
                <div className="px-4 py-5 text-sm text-gray-500">No verticals match that search.</div>
              ) : (
                filteredVerticals.map((vertical) => (
                  <button
                    key={vertical.vertical_id}
                    type="button"
                    onClick={() => openVertical(vertical.vertical_id)}
                    className="grid w-full grid-cols-1 gap-2 border-b border-gray-900 px-4 py-3 text-left text-xs hover:bg-gray-900/50 md:grid-cols-[minmax(0,1fr)_120px_minmax(0,1fr)_72px] md:gap-3"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm text-gray-100">{verticalLabel(vertical)}</span>
                      <span className="block text-gray-600">{vertical.vertical_id}</span>
                    </span>
                    <span className="min-w-0 self-center text-gray-400">{vertical.launch_mode}</span>
                    <span className="min-w-0 self-center truncate text-gray-500">
                      {vertical.customer_home_url ?? '-'}
                    </span>
                    <span className="self-center text-right text-[#39FF14]">Open</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
